<template>
    <TableRow :is-selected="isSelected" class="text-gray-700"
        @dblclick="toggleExpandRow"
        @mouseenter="handleRowEnter(2000)"
        @mouseleave="handleRowLeave"
    >
        <td class="border-r border-b border-gray-200 align-top">
            <div class="px-3 py-1.5 flex flex-col">
                <div class="flex flex-col ">
                    <div class="whitespace-nowrap flex flex-wrap gap-1 pb-1 justify-between tracking-wide items-center">
                        <div class="font-semibold text-black">
                            {{ clientTransaction.client.client_name }}
                        </div>
                    </div>
                </div>
                <div v-if="targetCompany" id="target-company-section"  class="w-full py-2">
                    <div class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Target Company:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>           
                        <div class="whitespace-nowrap flex flex-wrap gap-1 pb-1 justify-between tracking-wide items-center">
                            <div class="font-semibold text-black">
                                {{ targetCompany.company_name }}
                            </div>
                        </div>
                    </div>
                </div>
                <div id="transaction-date-section"  class="w-full py-2">
                    <div class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Transaction date:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>           
                        <div class="whitespace-nowrap flex flex-wrap gap-1 pb-1 justify-between tracking-wide items-center">
                            <div class="font-semibold text-black">
                                {{ transactionDate }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </td>
        <td class="border-r border-b border-gray-200 align-top">
            <div class="px-3 py-1.5 flex flex-col">
                <div id="contingency-payments-section"  class="w-full flex flex-col py-2 max-h-48 overflow-auto">
                    <div v-for="payment in clientTransaction.contingency_payments" :key="payment.id" class="flex w-full flex-col">
                        <div class="flex w-full justify-between ">
                            <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                                Payment Date:
                            </div>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>           
                            <div class="whitespace-nowrap flex flex-wrap gap-1 pb-1 justify-between tracking-wide items-center">
                                <div class="font-semibold text-black">
                                    {{ formatDateTwoDigitYear(payment.payment_date) }}
                                </div>
                            </div>
                        </div>
                        <div class="flex w-full justify-between ">
                            <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                                Status:
                            </div>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>           
                            <div class="whitespace-nowrap flex flex-wrap gap-1 pb-1 justify-between tracking-wide items-center">
                                <div class="font-semibold text-black">
                                    {{ payment.status }}
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col w-full pb-1">
                            <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                                Description:
                            </div>
                            <div class="whitespace-pre-wrap break-all">
                                {{ payment.contingency_description }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </td>
        <td class="border-r border-b border-gray-200 align-top">
            <div class="px-3 py-1.5 flex flex-col">
                <div id="tombstone-section"  class="w-full h-full">
                    <div v-if="clientTransaction.google_drive_tombstone_link" >
                        <div class="h-full w-full flex justify-center">
                            <iframe :src="clientTransaction.google_drive_tombstone_link" class="h-full w-20" allow="autoplay"></iframe>
                        </div>
                    </div>
                </div>
            </div>
        </td>
    </TableRow>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, computed, onMounted } from 'vue';
import ExpandSVG from '../svg-components/ExpandSVG.vue';
import LoadingButton from '../base-components/LoadingButton.vue';
import TableRow from '../base-components/TableRow.vue';
import ChevronDownMiniSVG from '../svg-components/ChevronDownMiniSVG.vue';
import ChevronUpMiniSVG from '../svg-components/ChevronUpMiniSVG.vue';
import { formatDateTwoDigitYear } from '@/composables/formatDate';

const props = defineProps([
    'clientTransaction', 
    'selectAll', 
    'expandAll',
    'clearedSelectedResults'
])

const emit = defineEmits([
    'open-row-details', 
    'select-row', 
    'deselect-row', 
    'update-crm-status',
    'perform-row-action',
])

const isSelected = ref(props.selectAll)
const expandedRow = ref(false)
const rowHover = ref(false)
const canSetRowHover = ref(false)
const canExpandRow = ref(true)
const haltEmit = ref(false)

const isNamePopoverVisible = ref(false)
const xPosition = ref(0)
const yPosition = ref(0)
const nameElement = ref(null)

const crmDetailsEndIndex = ref(2)

const transactionDate = computed(() => {
    return formatDateTwoDigitYear(props.clientTransaction.transaction_date)
})

const targetCompany = computed(() => {
    if(props.clientTransaction.target_company){
        return props.clientTransaction.target_company
    } else {
        return null
    }
})

const canShowBusinessInfoActions = ref(false)
const visibleBusinessInfoActions = ref(false)

const canShowCampaignActions = ref(false)
const visibleCampaignActions = ref(false)

const canShowCRMActions = ref(false)
const visibleCRMActions = ref(false)

const canShowContactActions = ref(false)
const visibleContactActions = ref(false)

const contactCardEnterFromClass = ref('transform translate-x-[160%] opacity-0')
const contactCardLeaveToClass = ref('transform -translate-x-[160%] opacity-0')

const rowBoundary = ref(null)
const businessInfoActionsMenuBoundary = ref(null)
const campaignActionsMenuBoundary = ref(null)
const crmActionsMenuBoundary = ref(null)
const contactActionsMenuBoundary = ref(null)

const initialRowHeight = ref(null)

onMounted(() => {
    // initialRowHeight.value = Math.round(rowBoundary.value.getBoundingClientRect().bottom - rowBoundary.value.getBoundingClientRect().top).toString()
})

const rowActionsOuterCellStyle = computed(() => {
    return ''
})

const rowActionsInnerCellStyle = computed(() => {
    return ''
})

const companyInfoOuterCellStyle = computed(() => {
    return ''
})

const companyInfoInnerCellStyle = computed(() => {
    return ''
})

const businessInfoOuterCellStyle = computed(() => {
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleBusinessInfoActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const businessInfoInnerCellStyle = computed(() => {
    let innerCellStyle = ''
    if(visibleBusinessInfoActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const campaignOuterCellStyle = computed(() => {
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleCampaignActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const campaignInnerCellStyle = computed(() => {
    let innerCellStyle = ''
    if(visibleCampaignActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const crmActionsOuterCellStyle = computed(() => {
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleCRMActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const crmActionsInnerCellStyle = computed(() => {
    let innerCellStyle = ''
    if(visibleCRMActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const contactsOuterCellStyle = computed(() => {
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleContactActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const contactsInnerCellStyle = computed(() => {
    let innerCellStyle = ''
    if(visibleContactActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const businessInfoActionsMenuStyle = computed(() => {
    if(visibleBusinessInfoActions.value && businessInfoActionsMenuBoundary.value) {
        let menuHeight = Math.round(businessInfoActionsMenuBoundary.value.getBoundingClientRect().bottom - businessInfoActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const campaignActionsMenuStyle = computed(() => {
    if(visibleCampaignActions.value && campaignActionsMenuBoundary.value) {
        let menuHeight = Math.round(campaignActionsMenuBoundary.value.getBoundingClientRect().bottom - campaignActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const crmActionsMenuStyle = computed(() => {
    if(visibleCRMActions.value && crmActionsMenuBoundary.value) {
        let menuHeight = Math.round(crmActionsMenuBoundary.value.getBoundingClientRect().bottom - crmActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const contactActionsMenuStyle = computed(() => {
    if(visibleContactActions.value && contactActionsMenuBoundary.value) {
        let menuHeight = Math.round(contactActionsMenuBoundary.value.getBoundingClientRect().bottom - contactActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const survivingCompany = computed(() => {
    return props.assimilationLog.surviving_company
})

const assimilatedCompany = computed(() => {
    return props.assimilationLog.assimilated_company
})

const survivingCompanyName = computed(() => {
    return survivingCompany.value.company_name ? survivingCompany.value.company_name : '';
})

const assimilatedCompanyName = computed(() => {
    return assimilatedCompany.value.company_name ? assimilatedCompany.value.company_name : '';
})


const survivingCompanyWebsiteURL = computed(() => {
    if(survivingCompany.value.website_url) {
        if (survivingCompany.value.website_url.startsWith('http://') || survivingCompany.value.website_url.startsWith('https://')) {
            return survivingCompany.value.website_url
        } else {
            return `http://${survivingCompany.value.website_url}`
        }
    }
    return null
})

const assimilatedCompanyWebsiteURL = computed(() => {
    if(assimilatedCompany.value.website_url) {
        if (assimilatedCompany.value.website_url.startsWith('http://') || assimilatedCompany.value.website_url.startsWith('https://')) {
            return assimilatedCompany.value.website_url
        } else {
            return `http://${assimilatedCompany.value.website_url}`
        }
    }
    return null
})



const menuIconFill = computed(() => {
    return '#99a1af'
})

function blockExpandRow() {
    canExpandRow.value = false
}

function unblockExpandRow() {
    canExpandRow.value = true
}

function showBusinessInfoActions(delay=null) {
    canShowBusinessInfoActions.value = true
    if(delay) {
        setTimeout(() => {
            if(canShowBusinessInfoActions.value) {
                visibleBusinessInfoActions.value = true
                rowHover.value = true
            }
        }, delay)
    } else {
        visibleBusinessInfoActions.value = true
        rowHover.value = true
    }

}

function expandRow() {
    expandedRow.value = true
    crmDetailsEndIndex.value = null
}

function collapseRow() {
    expandedRow.value = false
    crmDetailsEndIndex.value = 2
}

function toggleExpandRow() {
    console.log('toggleExpandRow', canExpandRow.value)
    if(canExpandRow.value) {
        if(expandedRow.value) {
            collapseRow()
        } else {
            expandRow()
        }
    }

}

function handleRowEnter(delay) {
    canSetRowHover.value = true
    if(delay) {
        setTimeout(() => {
            if(canSetRowHover.value) {
                rowHover.value = true
            }
        }, delay)
    } else {
        expandRow()
    }
}

function handleRowLeave() {
    canSetRowHover.value = false
    rowHover.value = false
}

function toggleSelected() {
    if(isSelected.value) {
        isSelected.value = false
        emit('deselect-row', props.company)
    } else {
        isSelected.value = true
        emit('select-row', props.company)
    }
}


function performRowAction(actionData) {
    actionData.company = props.company
    console.log('performRowAction', actionData)
    emit('perform-row-action', actionData)
}
</script>